import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Notification,
  Select,
} from "@cea/ui-kit";
import PropTypes from "prop-types";
import { useContext, useState } from "react";

import { FETCH_SUBSCRIPTIONS } from "../../queries/organizations";
import { NOTIFY_CLIENTS } from "../../queries/miServices";
import OrganizationContext from "../../contexts/OrganizationContext";
import { getNotification } from "../../utils";
import AuthContext from "../../contexts/AuthContext";

import TextEditor from "./TextEditor";

export const NotifyClientModal = ({ isVisible, handleCancel }) => {
  const [formSubmitLoading, setFormSubmitLoading] = useState(false);
  const [selectAllSubscribers, setSelectAllSubscribers] = useState(false);
  const [notifyMiClients] = useMutation(NOTIFY_CLIENTS);
  const [form] = Form.useForm();
  const { service, organization } = useContext(OrganizationContext);
  const authContext = useContext(AuthContext);

  const isEBRService = service?.serviceType === "ebr";

  const { data, loading: isLoading } = useQuery(FETCH_SUBSCRIPTIONS, {
    variables: { service: service?.id },
    skip: isEBRService,
    onError: (e) => {
      const message = getNotification(e);

      Notification.error({
        message,
      });
    },
  });

  const onFormSubmit = async () => {
    try {
      setFormSubmitLoading(true);
      const { emailContent, subscribers, subject, shouldSendAllSubscribers } =
        form.getFieldsValue();

      await notifyMiClients({
        variables: {
          emailContent,
          serviceID: service.id,
          orgIDs: subscribers,
          subject,
          shouldSendAllSubscribers,
          from: authContext.user?.email,
        },
        notifyOnNetworkStatusChange: true,
      });

      Notification.success({
        message: "Email sent to Subcribers",
      });

      form.resetFields();
    } catch (error) {
      const message = getNotification(error);

      Notification.error({
        message,
      });
    } finally {
      setFormSubmitLoading(false);
      handleCancel();
    }
  };

  const onSelectAll = () => {
    if (selectAllSubscribers) {
      form.setFieldsValue({
        subscribers: undefined,
      });
    } else {
      form.setFieldsValue({
        subscribers: data?.ceaSubscriptions?.subscriptions?.map(
          ({ organization }) => organization.id
        ),
      });
    }
    setSelectAllSubscribers((prevState) => !prevState);
  };

  return (
    <Modal
      title="Notify Clients"
      visible={isVisible}
      onOk={onFormSubmit}
      onCancel={handleCancel}
      centered
      footer={[
        <Button
          key="submit"
          loading={isLoading || formSubmitLoading}
          type="primary"
          onClick={onFormSubmit}
          className="ml-auto"
        >
          Send Email
        </Button>,
        <Button
          key="cancel"
          loading={isLoading}
          type="secondary"
          onClick={handleCancel}
        >
          Cancel
        </Button>,
      ]}
      bodyStyle={{ maxHeight: "500px", overflowY: "scroll" }}
      width="75%"
    >
      <Form
        form={form}
        {...(isEBRService && {
          initialValues: { subscribers: [organization.id] },
        })}
        name="basic"
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          label="Who should recieve this email?"
          name="subscribers"
          rules={[
            {
              required: true,
              message: "Please select subscriber",
            },
          ]}
        >
          <Select
            maxTagCount="responsive"
            mode="multiple"
            placeholder="Choose Subscribers"
            showSearch
            loading={isLoading}
            {...(isEBRService && {
              disabled: true,
            })}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: "4px 0" }} />
                <Button type="text" onClick={onSelectAll}>
                  {selectAllSubscribers ? "Deselect All" : "Select All"}
                </Button>
              </>
            )}
            options={
              isEBRService
                ? [
                    {
                      label: organization.name,
                      value: organization.id,
                    },
                  ]
                : data?.ceaSubscriptions?.subscriptions?.map(
                    ({ organization }) => ({
                      label: organization.name,
                      value: organization.id,
                    })
                  )
            }
          />
        </Form.Item>
        <Form.Item
          label="Subject"
          name="subject"
          rules={[
            {
              required: true,
              message: "Please enter the subject of email",
            },
          ]}
        >
          <Input placeholder="Add a subject" />
        </Form.Item>
        <Form.Item
          name="emailContent"
          rules={[
            {
              required: true,
              message: "Please enter body of post",
            },
          ]}
        >
          {/* @ts-ignore */}
          <TextEditor />
        </Form.Item>
      </Form>
    </Modal>
  );
};

NotifyClientModal.defaultProps = {
  parentId: null,
};

NotifyClientModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleOk: PropTypes.func.isRequired,
  parentId: PropTypes.string,
};

export default NotifyClientModal;
